import axios from 'axios';
import { Permission, PermissionFormData, PermissionStructure, UserPermissionsResponse } from '../types/permission';

const API_URL = `${process.env.REACT_APP_API_URL}/permissions`;

// الحصول على هيكل الصلاحيات المتاح (الأقسام والصفحات والإجراءات)
export const getPermissionStructure = async (): Promise<PermissionStructure> => {
  const response = await axios.get(`${API_URL}/structure`);
  return response.data;
};

// الحصول على جميع الصلاحيات حسب المجموعة (اختياري)
export const getPermissions = async (groupId?: string): Promise<Permission[]> => {
  const response = await axios.get(API_URL, {
    params: { groupId }
  });
  return response.data;
};

// الحصول على صلاحيات مجموعة محددة
export const getGroupPermissions = async (groupId: string): Promise<Permission> => {
  const response = await axios.get(`${API_URL}/${groupId}`);
  return response.data;
};

// إنشاء أو تحديث صلاحيات مجموعة
export const savePermissions = async (groupId: string, data: PermissionFormData): Promise<{ message: string, permissions: Permission }> => {
  const response = await axios.post(`${API_URL}/${groupId}`, data);
  return response.data;
};

// تحديث الصلاحيات (إضافة الجديد بدون مسح الموجود)
export const updatePermissions = async (groupId: string): Promise<{ success: boolean, message: string, permissions: Permission }> => {
  const response = await axios.put(`${API_URL}/${groupId}/update`);
  return response.data;
};

// حذف صلاحيات مجموعة
export const deletePermissions = async (groupId: string): Promise<{ message: string }> => {
  const response = await axios.delete(`${API_URL}/${groupId}`);
  return response.data;
};

// الحصول على صلاحيات مستخدم
export const getUserPermissions = async (userId: string): Promise<UserPermissionsResponse> => {
  const response = await axios.get(`${API_URL}/user/${userId}`);
  return response.data;
};

// التحقق من صلاحية معينة لقسم معين
export const checkPermission = (
  permissions: Permission | null | undefined,
  sectionId: string,
  pageId: string,
  actionId: string
): boolean => {
  console.log(`التحقق من صلاحية: القسم=${sectionId}, الصفحة=${pageId}, الإجراء=${actionId}`);
  
  try {
    // إذا لم تكن هناك صلاحيات، ارجع false
    if (!permissions || !permissions.sections) {
      console.log("لا توجد صلاحيات مخزنة");
      return false;
    }

    // طباعة هيكل الصلاحيات للتشخيص
    console.log(`هيكل الصلاحيات: ${JSON.stringify(permissions.sections.map(s => s.identifier))}`);
    
    // البحث عن القسم
    const section = permissions.sections.find(s => s.identifier === sectionId);
    if (!section) {
      console.log(`لم يتم العثور على القسم: ${sectionId}`);
      return false;
    }

    console.log(`صفحات القسم: ${JSON.stringify(section.pages.map(p => p.identifier))}`);
    
    // البحث عن الصفحة
    const page = section.pages.find(p => p.identifier === pageId);
    
    // قائمة بصفحات الإعدادات الخاصة بقسم الفواتير
    const specialInvoicePages = ['settings', 'print-templates', 'product-barcode'];
    
    // التعامل مع صفحات إعدادات الفواتير الخاصة
    if (!page && specialInvoicePages.includes(pageId) && sectionId === 'invoices') {
      console.log(`[permissionService] صفحة خاصة بإعدادات الفواتير: ${pageId}`);
      
      // إذا كان لدى المستخدم صلاحية عامة للوصول إلى قسم الفواتير، نسمح بالوصول
      if (actionId === 'view_page' && section.visible) {
        console.log(`[permissionService] السماح بالوصول لصفحة ${pageId} لأن لديه صلاحية للقسم`);
        return true;
      }
    }
    
    // التعامل مع الصفحات الديناميكية غير الموجودة
    // إذا كانت الصفحة تبدأ بـ "page-" وغير موجودة، فهذه على الأرجح صفحة فاتورة ديناميكية
    if (!page && pageId.startsWith('page-')) {
      console.log(`لم يتم العثور على الصفحة الديناميكية: ${pageId}، سنحاول التعامل مع هذه الحالة`);
      
      // إذا كان الإجراء هو عرض الصفحة فقط وكان القسم مرئي، نسمح بالوصول
      if (actionId === 'view_page' && section.visible) {
        console.log(`السماح بالوصول لصفحة ${pageId} غير الموجودة لأن القسم مرئي`);
        return true;
      }
      
      // لإجراءات أخرى، نبحث عن صفحة settings كمرجع للصلاحيات
      const settingsPage = section.pages.find(p => p.identifier === 'settings');
      if (settingsPage && settingsPage.visible) {
        // البحث عن الإجراء في صفحة الإعدادات
        const settingAction = settingsPage.actions.find(a => a.identifier === 'view_page');
        
        // إذا كان المستخدم لديه صلاحية الوصول لصفحة الإعدادات، نفترض أن لديه صلاحية مماثلة هنا
        if (settingAction && settingAction.allowed) {
          console.log(`استخدام صلاحيات صفحة الإعدادات كمرجع للصفحة غير الموجودة ${pageId}`);
          return true;
        }
      }
    }

    // التعامل مع الصفحات الديناميكية للسندات المتخصصة غير الموجودة
    // إذا كانت الصفحة تبدأ بـ "voucher-" وغير موجودة، فهذه على الأرجح صفحة سند متخصص ديناميكي
    if (!page && pageId.startsWith('voucher-')) {
      console.log(`لم يتم العثور على الصفحة الديناميكية للسند المتخصص: ${pageId}، سنحاول التعامل مع هذه الحالة`);
      
      // إذا كان الإجراء هو عرض الصفحة فقط وكان القسم مرئي، نسمح بالوصول
      if (actionId === 'view_page' && section.visible) {
        console.log(`السماح بالوصول لصفحة ${pageId} غير الموجودة لأن القسم مرئي`);
        return true;
      }
      
      // لإجراءات أخرى، نبحث عن صفحة specialized-voucher-templates كمرجع للصلاحيات
      const voucherSettingsPage = section.pages.find(p => p.identifier === 'specialized-voucher-templates');
      if (voucherSettingsPage && voucherSettingsPage.visible) {
        // البحث عن الإجراء في صفحة إعدادات السندات المتخصصة
        const voucherSettingAction = voucherSettingsPage.actions.find(a => a.identifier === 'view_page');
        
        // إذا كان المستخدم لديه صلاحية الوصول لصفحة إعدادات السندات المتخصصة، نفترض أن لديه صلاحية مماثلة هنا
        if (voucherSettingAction && voucherSettingAction.allowed) {
          console.log(`استخدام صلاحيات صفحة إعدادات السندات المتخصصة كمرجع للصفحة غير الموجودة ${pageId}`);
          return true;
        }
      }
    }
    
    if (!page) {
      console.log(`لم يتم العثور على الصفحة: ${pageId}`);
      return false;
    }

    // التحقق من مرئية القسم (ضروري للوصول إلى أي صفحة أو إجراء)
    if (!section.visible) {
      console.log(`القسم ${sectionId} غير مرئي، إرجاع false`);
      return false;
    }

    // التحقق من مرئية الصفحة (ضروري للوصول إلى أي إجراء)
    if (!page.visible) {
      console.log(`الصفحة ${pageId} غير مرئية، إرجاع false`);
      return false;
    }

    // إذا كان الإجراء هو عرض الصفحة، وكانت الصفحة مرئية، فنعتبر الصلاحية موجودة
    if (actionId === 'view_page') {
      console.log(`الصفحة ${pageId} مرئية ومسموح بالوصول إليها`);
      return true;
    }

    // المرئية فقط تسمح بعرض العناصر، لكن يجب التحقق من صلاحية الإجراء المحدد
    console.log(`إجراءات الصفحة: ${JSON.stringify(page.actions.map(a => a.identifier))}`);
    
    // طباعة الإجراءات المتاحة للصفحة للتشخيص
    console.log(`إجراءات الصفحة ${pageId}: ${JSON.stringify(page.actions.map(a => ({ id: a.identifier, allowed: a.allowed })))}`);
    
    // البحث عن الإجراء
    const action = page.actions.find(a => a.identifier === actionId);
    if (!action) {
      console.log(`لم يتم العثور على الإجراء: ${actionId} في صفحة ${pageId}`);
      return false;
    }

    // سجل نتيجة التحقق
    console.log(`تم العثور على الإجراء ${actionId} في صفحة ${pageId} بحالة allowed=${action.allowed}`);
    
    // ارجع قيمة السماح للإجراء
    return Boolean(action.allowed);
  } catch (error) {
    console.error("خطأ في التحقق من الصلاحيات:", error);
    return false;
  }
};

export const permissionsApi = {
  getPermissionStructure,
  getPermissions,
  getGroupPermissions,
  savePermissions,
  updatePermissions,
  deletePermissions,
  getUserPermissions,
  checkPermission
};